import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import CommentForm from '../components/CommentForm/CommentForm';
import CommentList from '../components/CommentList/CommentList';
import Rating from '../components/Rating/Rating';
import Gallery from '../components/Gallery/Gallery';
import PostMeta from '../components/PostMeta/PostMeta';
import TagsList from '../components/TagsList/TagsList';
import BackButton from '../components/BackButton/BackButton';
import styles from './PostPage.module.css';
import useFetchPost from "../hooks/useFetchPost";
import { useAuth } from "../hooks/useAuth";
// Интерфейсы для типизации
interface Comment {
    id: number;
    author: string;
    text: string;
    date: string;
    time: string;
}
const PostPage = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const [comments, setComments] = useState<Comment[]>([]);
    const [rating, setRating] = useState(0);
    const { user } = useAuth();
    const postIdNumber = id ? parseInt(id) : -1;
    const { post, loading, error } = useFetchPost(postIdNumber);
    useEffect(() => {
        if (!id) return;
        const savedCommentsStr = localStorage.getItem(`post-${id}-comments`);
        const savedComments = savedCommentsStr ? JSON.parse(savedCommentsStr) : [];
        const savedRatingStr = localStorage.getItem(`post-${id}-rating`);
        const savedRating = savedRatingStr ? parseFloat(savedRatingStr) : 0;
        setComments(savedComments);
        setRating(savedRating);
    }, [id]);

    const handleAddComment = (newComment: { author: string; text: string }) => {
        const updatedComments = [...comments, {
            id: Date.now(),
            ...newComment,
            date: new Date().toLocaleDateString('ru-RU'),
            time: new Date().toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' })
        }];
        setComments(updatedComments);
        localStorage.setItem(`post-${id}-comments`, JSON.stringify(updatedComments));
    };
    const handleRatingChange = (newRating: number) => {
        setRating(newRating);
        if (id) {
            localStorage.setItem(`post-${id}-rating`, newRating.toString());
        }
    };
    if (loading) {
        return <div className={styles.loading}>Загрузка поста...</div>;
    }
    if (error) {
        return (
            <div className={styles.error}>
                Ошибка: {error}
                <BackButton>Вернуться на главную</BackButton>
            </div>
        );
    }
    if (!post) {
        return (
            <div className={styles.notFound}>
                Пост не найден
                <BackButton>Вернуться к статьям</BackButton>
            </div>
        );
    }
    // Преобразуем images в массив объектов, если это массив строк
    const images = Array.isArray(post.images)
        ? post.images.map(img => typeof img === 'string' ? { url: img } : img)
        : [];
    return (
        <div className={styles.postPage}>
            <div className={styles.controls}>
                <BackButton>&larr; Назад к статьям</BackButton>
                {user && user.id === post.author_id && (
                    <button
                        onClick={() => navigate(`/edit-post/${id}`)}
                        className={styles.editButton}
                    >
                        Редактировать
                    </button>
                )}
            </div>
            <article className={styles.post}>
                <PostMeta
                    category={post.category.name}
                    author={post.author}
                    authorId={post.author_id}
                    date={new Date(post.date).toLocaleDateString('ru-RU')}
                />
                <h1 className={styles.title}>{post.title}</h1>
                <Gallery images={images} />
                <div
                    className={styles.content}
                    dangerouslySetInnerHTML={{ __html: post.content }}
                />
                <TagsList tags={post.tags || []} />
            </article>
            <div className={styles.ratingSection}>
                <h2>Оцените статью</h2>
                <Rating value={rating} onChange={handleRatingChange} />
                <div className={styles.ratingValue}>{rating.toFixed(1)}/5.0</div>
            </div>
            <div className={styles.commentsSection}>
                <h2>Комментарии ({comments.length})</h2>
                <CommentForm onSubmit={handleAddComment} />
                <CommentList comments={comments} />
            </div>
        </div>
    );
};
export default PostPage;